import { useEffect } from 'react';
import window from 'global/window';
import _ from 'lodash';

import { useStores } from 'src/hooks/use-stores';
import { hideZendeskWidget, closeZendeskWidget, showDecagonWidget } from 'shared/helpers/tools';
import { fetchDecagonUserAuth } from 'shared/helpers/decagon';

type DecagonWidget = {
  hide: () => void;
  setMetadata: (metadata: { email: string | null | undefined; name: string; userType: 'admin' }) => void;
  setUserId: (userId: string | null | undefined) => void;
  setUserIdAuth: (signature: string, epoch: number) => void;
};

type AdminUser = {
  email: string | null | undefined;
  fullName: string;
  id: string | null | undefined;
};

declare global {
  // Window declaration merging requires an interface.
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    __PLAYWRIGHT_TEST__?: boolean;
    duet?: DecagonWidget;
  }
}

export default function useAdminChatWidget(): void {
  const { User, apolloClient } = useStores();

  useEffect(() => {
    closeZendeskWidget();
    hideZendeskWidget();
    return initializeDecagon(User, apolloClient);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function initializeDecagon(user: AdminUser, apolloClient: unknown): () => void {
  // Skip Decagon in E2E test environments - the widget's high z-index (99999999)
  // interferes with MUI modals during Playwright tests
  // eslint-disable-next-line @typescript-eslint/prefer-optional-chain -- preserve the legacy environment guard
  if (typeof window !== 'undefined' && window.__PLAYWRIGHT_TEST__) {
    // eslint-disable-next-line @typescript-eslint/unbound-method -- lodash noop has no receiver state
    return _.noop;
  }

  if (!window.duet) {
    const decagonInitScript = document.createElement(`script`);
    decagonInitScript.id = `chat-loader`;
    decagonInitScript.src = `https://decagon.ai/loaders/dutchie.js`;
    decagonInitScript.async = true;
    decagonInitScript.onload = async () => {
      // Decagon establishes the conversation the moment setUserId fires, so the signed
      // (signature, epoch) must be applied in the same turn — otherwise the conversation
      // is tagged _decagon_has_valid_signature:false and never recovers. Lower envs
      // without the HMAC secret return nulls and we fall through to unsigned.
      const auth = await fetchDecagonUserAuth(apolloClient);
      const { duet } = window;
      if (!duet) {
        throw new Error('Decagon script loaded without initializing window.duet');
      }

      duet.setUserId(user.id);
      if (auth?.signature && typeof auth.epoch === 'number') {
        duet.setUserIdAuth(auth.signature, auth.epoch);
      }

      duet.setMetadata({
        email: user.email,
        name: user.fullName,
        userType: 'admin',
      });
    };
    document.body.appendChild(decagonInitScript);
  }

  // make sure it's not hidden from before
  showDecagonWidget();

  return () => window.duet?.hide();
}
